import { pipe } from 'fp-ts/function';
import { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { getFormValues } from 'redux-form';
import { Card, withAsyncContainer } from '@mews-commander/ui';
import { Stack, StackItem } from '@optimus-web/core';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { PRODUCT_SERVICE_FORM_NAME } from '../../constants';
import { hasFailed, isFetching } from '../../selectors';
import CreateServiceForm from '../forms/CreateServiceForm';
import ProductServiceTotalRow from '../ProductServiceTotalRow';
import * as fieldNames from '../../constants';
import { ProductServiceOrderFormProps, ServiceFormValues } from '../../types';
import { addProductServiceOrder } from '../../actions/actions';
import { ProductServiceOrderProductList } from './ProductServiceOrderProductList';

const ProductServiceOrderCreatePageBody: FC<ProductServiceOrderFormProps> = ({
    serviceId,
    customerId,
    billId,
    guestIds,
    allowNegativeValues,
    onlyOfferedToEmployee,
}) => {
    const dispatch = useCommanderThunkDispatch();
    const createFormValues = useCommanderSelector(getFormValues(PRODUCT_SERVICE_FORM_NAME)) as ServiceFormValues;

    const service = createFormValues?.[fieldNames.SERVICE_ID];

    const selectedServiceId = service ?? serviceId;

    const onSubmit = useCallback(
        (values: ServiceFormValues) => {
            const { customerId: customerIdValue, serviceId: serviceIdValue, ...others } = values;
            if (!customerIdValue || !serviceIdValue) {
                return;
            }

            return dispatch(
                addProductServiceOrder({
                    billId,
                    values: { customerId: customerIdValue, serviceId: serviceIdValue, ...others },
                })
            );
        },
        [billId, dispatch]
    );

    return (
        <Stack spacing="200" distribution="stretch">
            <StackItem>
                <Card>
                    <CreateServiceForm
                        customerId={customerId}
                        serviceId={selectedServiceId}
                        guestIds={guestIds}
                        form={PRODUCT_SERVICE_FORM_NAME}
                        customerDisabled={!!serviceId}
                        serviceDisabled={!!serviceId}
                        onSubmit={onSubmit}
                        onlyOfferedToEmployee={onlyOfferedToEmployee}
                    />
                </Card>
            </StackItem>
            <StackItem>
                {selectedServiceId && (
                    <Card>
                        <Stack vertical>
                            <ProductServiceOrderProductList
                                serviceId={selectedServiceId}
                                allowNegativeValues={allowNegativeValues}
                            />
                            <ProductServiceTotalRow serviceId={selectedServiceId} />
                        </Stack>
                    </Card>
                )}
            </StackItem>
        </Stack>
    );
};

export default pipe(
    ProductServiceOrderCreatePageBody,
    withAsyncContainer,
    connect(
        createStructuredSelector({
            isFetching,
            hasFailed,
        })
    )
);
